import { BackgroundTrackId } from "./types";

export type BackgroundTrack = {
    id: BackgroundTrackId;
    /** i18n key for the name a therapist picks it by. */
    labelKey: string;
    /** Where the file lives, served from our own origin. Null for "no music". */
    src: string | null;
    /** i18n key for the provenance line, shown wherever the track is chosen. */
    creditKey: string | null;
};

/**
 * The beds a meditation can be laid over: silence, and whatever the therapist
 * brings.
 *
 * We shipped three synthesised ones so a bed was one press away. They were not
 * good enough to guide somebody through — a bed you would not use yourself is
 * worse than none, because it is the one thing the patient hears for twenty
 * minutes — so the offered list is silence, and music of their own lives in
 * their library, where they said it was theirs to use.
 *
 * Values MUST stay in sync with `domain.models.meditation.BackgroundTrack`
 * on the API.
 */
export const BACKGROUND_TRACKS: readonly BackgroundTrack[] = [
    {
        id: BackgroundTrackId.NONE,
        labelKey: "meditation.tracks.none",
        src: null,
        creditKey: null,
    },
];

/**
 * Beds that are no longer offered but still play.
 *
 * The three we made left the list when the therapist's own library arrived.
 * A meditation saved with one keeps playing it, and keeps saying so — a
 * retired option that silently became "no music" would be us editing somebody
 * else's work.
 *
 * Still served from our own origin, because the studio's preview and the
 * session both reach them through the Web Audio API, where a cross-origin
 * source without CORS headers goes silent rather than failing.
 */
const RETIRED: readonly BackgroundTrack[] = [
    {
        id: BackgroundTrackId.OCEAN,
        labelKey: "meditation.tracks.ocean",
        src: "/audio/meditation/ocean.wav",
        creditKey: "meditation.tracks.credit",
    },
    {
        id: BackgroundTrackId.FOREST,
        labelKey: "meditation.tracks.forest",
        src: "/audio/meditation/forest.wav",
        creditKey: "meditation.tracks.credit",
    },
    {
        id: BackgroundTrackId.SINGING_BOWLS,
        labelKey: "meditation.tracks.singingBowls",
        src: "/audio/meditation/singing-bowls.wav",
        creditKey: "meditation.tracks.credit",
    },
];

export function backgroundTrack(id?: BackgroundTrackId | null): BackgroundTrack {
    return (
        BACKGROUND_TRACKS.find((track) => track.id === id) ??
        RETIRED.find((track) => track.id === id) ??
        BACKGROUND_TRACKS[0]
    );
}

/** The file to play for a track, or null when the choice is silence. */
export function backgroundTrackSrc(id?: BackgroundTrackId | null): string | null {
    return backgroundTrack(id).src;
}

/**
 * How loud the bed sits under a voice.
 *
 * Low enough that a quiet sentence is never competing with it. The voice is
 * the meditation; the music is the room it happens in.
 */
export const BACKGROUND_TRACK_GAIN = 0.25;
